import type * as Geo from "@/base/geo"
import type { EntityState, PayloadAction, Reducer } from "@reduxjs/toolkit"

import { loadActiveFarmAsync } from "@/base/load-app"
import { createEntityAdapter, createSlice } from "@reduxjs/toolkit"

import { signOutAsync } from "./auth.reducer"
import type { RootState } from "@/redux/root.reducer"

/**
 * The pivot's allowed travel arc, expressed as a paired start+end. A
 * partial arc has both endpoints set (degrees clockwise from north). A
 * full-circle pivot has no boundary — represented as `null` on the parent
 * CenterPivot (NOT as `{startDegrees: 0, endDegrees: 360}`). Keeping these
 * grouped in a single object makes it impossible at the type level to have
 * a half-set state.
 */
export interface Boundary {
  startDegrees: number
  endDegrees: number
}

/**
 * Represents a physical center pivot irrigation system.
 *
 * This is separate from device configurations - a CenterPivot can have
 * at most one controller device and one monitor device linked to it.
 */
/**
 * A named point of interest on a center pivot. The `location` is the
 * source of truth; `headingDegrees` is annotated by the backend (derived
 * from the parent pivot's center via ST_Azimuth) so the frontend doesn't
 * need to recompute it.
 */
export interface CenterPivotSlot {
  id: number
  name: string
  location: Geo.PointGeoJson
  /** Bearing in degrees clockwise from north — derived from the parent
   * pivot's center. Null when the parent has no center configured. */
  headingDegrees: number | null
}

/** The three PID gains, as the control panel edits them. */
export interface SpeedPidGains {
  proportional: number
  integral: number
  derivative: number
}

export interface CenterPivot {
  id: number
  name: string
  make: string | null
  model: string | null
  farmId: number
  pivotCenterGpsLocation: Geo.PointGeoJson | null
  pivotRadiusMeters: number | null
  /** Slots on the pivot, ordered by their arc position (boundary-shifted
   * heading) so adjacent entries are clockwise-adjacent. */
  slots: CenterPivotSlot[]
  /** Radius of the end gun spray in meters (default 40m) */
  endGunThrowRadiusMeters: number
  /** Boundary arc, or null for a full-circle pivot. Both endpoints in the
   * `Boundary` object are required when the field is non-null — there's no
   * representable half-set state. Translated from the API's flat
   * `boundary_start_degrees` / `boundary_end_degrees` pair via
   * `prepareCenterPivot`. */
  boundary: Boundary | null
  hasDirectionControl: boolean
  hasSpeedControl: boolean
  hasEndGun: boolean
  hasWetDryControl: boolean
  /** True when the controller has a pulsed speed sensor wired AND
   * calibrated (port + meters-per-pulse). Both are required — the
   * controller only builds its speed sensor when it has both, and
   * reports no error when it doesn't. */
  hasSpeedFeedback: boolean
  /** True when this pivot can actually be commanded to hold a speed:
   * it both measures speed and has PID gains to regulate with. The
   * control panel gates its "hold speed" mode on this, since a pivot
   * missing either half would have the command refused outright. */
  canRegulateSpeed: boolean
  /** Average flow rate in liters per minute (for speed-by-depth calculation) */
  averageFlowRateLpm: number | null
  /** Tip linear speed at the controller's maximum (100%) setting, in meters/hour. */
  maxSpeedMetersPerHour: number | null
  /** Tip linear speed at the slowest non-zero controller setting, in meters/hour. */
  minSpeedMetersPerHour: number | null
  /** Calibrated starting controller percent for pivots without speed
   * feedback. The backend's closed-loop adjuster nudges from here as
   * the run progresses. */
  initialSpeedPercent: number | null
  /** PID tuning for controller-side closed-loop speed regulation, used
   * by pivots that have speed feedback. Held in device RAM only, so the
   * backend re-sends these on every run start. Null means untuned —
   * the pivot falls back to running by percent, because commanding a
   * speed target with no gains is refused by the controller.
   *
   * Per-pivot by nature: the gain inverts the plant response
   * (m/min per valve %), which varies with pivot length, water
   * pressure, terrain and the valve's own range. */
  speedPidProportionalGain: number | null
  speedPidIntegralGain: number | null
  /** Prefer 0 — the controller's derivative filter uses a fixed 1s time
   * constant that isn't scaled to the multi-second sample period, so a
   * non-zero value adds noise rather than damping. */
  speedPidDerivativeGain: number | null
  /** Suggested tuning the control panel pre-fills when this pivot has
   * none saved. Served by the backend so the numbers have one source
   * of truth rather than being duplicated client-side. */
  defaultSpeedPidGains: SpeedPidGains
  /** Notify-on-stop threshold: arc (meters at the tip) the pivot must
   * cover within each detection window or the operator gets a stopped
   * notification. Null = feature off. */
  stoppedDetectionMinDistanceMeters: number | null
  /** Notify-on-stop time horizon: the window (minutes) the minimum
   * distance is judged over. Always present (default 30); only
   * meaningful when the feature is on. */
  stoppedDetectionDurationMinutes: number
  controllerDeviceId: string | null
  monitorDeviceId: string | null
  isActive: boolean
  createDate?: string
}

/**
 * Shape of a CenterPivot as it arrives from the API (after `camelcaseKeys`),
 * before normalization. The boundary-pair invariant lives on the
 * `CenterPivot` entity; this type captures the raw flat representation so
 * `prepareCenterPivot` can validate during ingest.
 */
export interface CenterPivotPayload extends Omit<CenterPivot, "boundary"> {
  boundaryStartDegrees: number | null
  boundaryEndDegrees: number | null
}

/**
 * Normalize an API-shaped CenterPivot payload into the entity stored in
 * redux. Validates the boundary-pair invariant: both flat fields null =
 * full circle (null boundary); both set = partial arc (object boundary);
 * a half-set state is malformed and falls back to null with a warning.
 */
export function prepareCenterPivot(payload: CenterPivotPayload): CenterPivot {
  const { boundaryStartDegrees, boundaryEndDegrees, ...rest } = payload
  let boundary: Boundary | null = null
  if (boundaryStartDegrees != null && boundaryEndDegrees != null) {
    boundary = {
      startDegrees: boundaryStartDegrees,
      endDegrees: boundaryEndDegrees,
    }
  } else if (
    (boundaryStartDegrees == null) !== (boundaryEndDegrees == null)
  ) {
    // Half-set boundary is malformed (the backend has a CHECK constraint
    // preventing this on writes; this catches stale data or hand-edited
    // rows). Treat as full circle.
    // eslint-disable-next-line no-console
    console.warn(
      `[CenterPivot ${payload.id}] boundary fields half-set ` +
        `(start=${boundaryStartDegrees}, end=${boundaryEndDegrees}); ` +
        `treating as full circle.`,
    )
  }
  return { ...rest, boundary }
}

const adapter = createEntityAdapter<CenterPivot, number>({
  selectId: (pivot) => pivot.id,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
})

const initialState = adapter.getInitialState()

const centerPivotsSlice = createSlice({
  name: "centerPivots",
  initialState,
  reducers: {
    upsertCenterPivot: (
      state,
      action: PayloadAction<CenterPivotPayload>,
    ) => {
      adapter.upsertOne(state, prepareCenterPivot(action.payload))
    },
    upsertManyCenterPivots: (
      state,
      action: PayloadAction<CenterPivotPayload[]>,
    ) => {
      adapter.upsertMany(state, action.payload.map(prepareCenterPivot))
    },
    removeCenterPivot: adapter.removeOne,
  },
  extraReducers: (builder) =>
    builder
      .addCase(signOutAsync.fulfilled, () => {
        return initialState
      })
      .addCase(loadActiveFarmAsync.fulfilled, (state, { payload }) => {
        if (payload.centerPivots) {
          adapter.setAll(state, payload.centerPivots.map(prepareCenterPivot))
        }
      }),
})

export const { upsertCenterPivot, upsertManyCenterPivots, removeCenterPivot } =
  centerPivotsSlice.actions

// Selectors
const selectors = adapter.getSelectors<RootState>(
  (state) => state.centerPivots,
)

export const {
  selectAll: selectAllCenterPivots,
  selectById: selectCenterPivotById,
  selectIds: selectCenterPivotIds,
  selectEntities: selectCenterPivotEntities,
  selectTotal: selectCenterPivotTotal,
} = selectors

/**
 * Get the CenterPivot associated with a device configuration, if any.
 */
export function selectCenterPivotForDevice(
  state: RootState,
  centerPivotId: number | null | undefined,
): CenterPivot | undefined {
  if (!centerPivotId) return undefined
  return selectCenterPivotById(state, centerPivotId)
}

/**
 * Find the CenterPivot whose controllerDeviceId matches the given device id.
 * Used by control-state contexts that already know the controller device id
 * (and need access to slot/boundary metadata for plan/ETA computation).
 */
export function selectCenterPivotByControllerDeviceId(
  state: RootState,
  controllerDeviceId: string | null | undefined,
): CenterPivot | undefined {
  if (!controllerDeviceId) return undefined
  for (const pivot of selectAllCenterPivots(state)) {
    if (pivot.controllerDeviceId === controllerDeviceId) return pivot
  }
  return undefined
}

/**
 * Check if a device should show the pivot outline.
 * - Legacy center_pivot type always shows
 * - center_pivot_controller always shows
 * - center_pivot_monitor only shows if no controller exists for the same pivot
 */
export function shouldShowPivotOutline(
  installationType: string | null | undefined,
  centerPivot: CenterPivot | null | undefined,
): boolean {
  // Legacy type always shows
  if (installationType === "center_pivot") return true

  // Controller always shows
  if (installationType === "center_pivot_controller") return true

  // Monitor only shows if no controller for same pivot
  if (installationType === "center_pivot_monitor") {
    return centerPivot != null && !centerPivot.controllerDeviceId
  }

  return false
}

export const centerPivotsReducer: Reducer<EntityState<CenterPivot, number>> =
  centerPivotsSlice.reducer
